import React, { useState, useEffect } from 'react';
import { WorkItemTable } from './workitems/WorkItemTable';
import { WorkItem, JobWithNotes, Appointment } from './workitems/workItemTypes';
import { useWorkItemData } from '../hooks/useWorkItemData';
import './WorkItemManagement.css';

export const WorkItemManagement: React.FC = () => {
  // Simulate current logged-in user - in a real app, this would come from authentication context
  // Available users: 'Steve Thomas', 'Jason Whitby', 'Tash Thomas', 'Doug Ingram'
  const currentUser = 'Steve Thomas';

  const { workItems: apiWorkItems, isLoading, error, refresh } = useWorkItemData();

  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [selectedWorkItem, setSelectedWorkItem] = useState<WorkItem | null>(null);

  // Sync local state to API data, filtering out resolved/closed items
  useEffect(() => {
    const active = apiWorkItems.filter(workItem =>
      workItem.status !== 'resolved' && workItem.status !== 'closed' && workItem.status !== 'completed'
    );
    setWorkItems(active);
  }, [apiWorkItems]);

  const handleWorkItemSelect = (workItem: WorkItem) => {
    setSelectedWorkItem(selectedWorkItem?.id === workItem.id ? null : workItem);
  };

  const handleWorkItemUpdate = (updatedWorkItem: WorkItem) => {
    setWorkItems(prevWorkItems =>
      prevWorkItems.map(workItem =>
        workItem.id === updatedWorkItem.id ? updatedWorkItem : workItem
      )
    );
  };

  const handleJobUpdate = (updatedJob: JobWithNotes) => {
    // No-op: jobs are not tracked separately after migration
  };

  const handleAppointmentUpdate = (updatedAppointment: Appointment) => {
    // No-op: appointments are not tracked separately after migration
  };

  const handleWorkItemReorder = (reorderedWorkItems: WorkItem[]) => {
    setWorkItems(reorderedWorkItems);
  };

  const handleWorkItemReassign = (workItemId: string, newAssignee: string) => {
    console.log(`Work Item ${workItemId} reassigned to ${newAssignee}`);
  };

  if (isLoading) {
    return (
      <div className="workitem-management-app">
        <div className="loading">Loading work items...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workitem-management-app">
        <div className="error">
          <h3>Error loading work items</h3>
          <p>{error}</p>
          <button type="button" onClick={refresh}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="workitem-management-app">
      <div className="workitem-management-container">
        <div className="main-content">
          <header className="content-header">
            <div className="header-content">
              <h1>Work Item Management System</h1>
              <div className="header-stats">
                <span className="stat-item">
                  Active Work Items: <strong>{workItems.length}</strong>
                </span>
                <span className="stat-item">
                  New: <strong>{workItems.filter(w => w.status === 'new').length}</strong>
                </span>
                <span className="stat-item">
                  In Progress: <strong>{workItems.filter(w => w.status === 'in-progress').length}</strong>
                </span>
                <span className="stat-item">
                  Waiting for Parts: <strong>{workItems.filter(w => w.status === 'waiting-for-parts').length}</strong>
                </span>
                <span className="stat-item">
                  Waiting on Customer: <strong>{workItems.filter(w => w.status === 'waiting-on-customer').length}</strong>
                </span>
              </div>
            </div>
          </header>

          <WorkItemTable
            workItems={workItems}
            jobs={[]}
            appointments={[]}
            selectedWorkItem={selectedWorkItem}
            onWorkItemSelect={handleWorkItemSelect}
            onWorkItemUpdate={handleWorkItemUpdate}
            onJobUpdate={handleJobUpdate}
            onAppointmentUpdate={handleAppointmentUpdate}
            onWorkItemReorder={handleWorkItemReorder}
            onWorkItemReassign={handleWorkItemReassign}
            currentUser={currentUser}
          />
        </div>
      </div>
    </div>
  );
};
